import { useMemo } from "react";
import { useAppSelector } from "@store/index";
import { donutChartConfigSelector } from "@store/slices/donut-chart-config-slice";
import { getTimePeriodTitleSuffix } from "@utils/time-period-display";
import { getPointInTimeUnitForPeriod } from "@utils/time-period-utils";

const DEFAULT_DONUT_TITLE = "Market Share Analysis";

interface EmphasisOption {
  value: string;
  label: string;
  type: "individual" | "group" | "dynamic" | "default";
}

function hasInvalidGroupShape(group: {
  sliceNames: string[];
  name: string;
}): boolean {
  if (group.sliceNames.length > 0 && group.sliceNames.length < 2) return true;
  if (group.sliceNames.length >= 2 && !group.name.trim()) return true;
  return false;
}

export function useDonutChartDerivedState(dataAvailabilityDate?: string | null) {
  const { currentConfig, question: questions } = useAppSelector(
    donutChartConfigSelector,
  );

  const getEmphasisOptions = (): EmphasisOption[] => {
    const options: EmphasisOption[] = [];

    // Individual visible slice options (actual slice names)
    currentConfig.series[0]?.data.forEach((slice) => {
      options.push({
        value: `slice:${slice.name}`,
        label: slice.name,
        type: "individual",
      });
    });

    // Group slice options
    currentConfig.groups.forEach((group) => {
      if (group.name.trim() && group.sliceNames.length >= 2) {
        options.push({
          value: `group:${group.id}`,
          label: `${group.name} (Group)`,
          type: "group",
        });
      }
    });

    options.push({ value: "highest", label: "Highest Percentage", type: "dynamic" });
    options.push({ value: "none", label: "None", type: "default" });

    return options;
  };

  const getUsedSlices = (): string[] => {
    const used: string[] = [];
    currentConfig.groups.forEach((group) => {
      used.push(...group.sliceNames);
    });
    return used;
  };

  const isValidationError = currentConfig.groups.some(hasInvalidGroupShape);

  const getValidationTooltip = (): string => {
    const invalidGroups = currentConfig.groups.filter(hasInvalidGroupShape);
    if (invalidGroups.length === 0) return "";

    const messages: string[] = [];
    invalidGroups.forEach((group) => {
      if (group.sliceNames.length > 0 && group.sliceNames.length < 2) {
        messages.push("Groups must have at least 2 slices");
      }
      if (group.sliceNames.length >= 2 && !group.name.trim()) {
        messages.push("Groups with slices must have a name");
      }
    });

    return [...new Set(messages)].join(". ");
  };

  const isAvailableData =
    !currentConfig.series[0]?.data || currentConfig.series[0].data.length === 0;

  const headerTitle = useMemo(() => {
    if (!questions?.name) return "Configure your donut chart";

    const hasCustomTitle =
      currentConfig.title !== "" &&
      currentConfig.title !== DEFAULT_DONUT_TITLE &&
      currentConfig.title !== questions.name;

    if (hasCustomTitle) {
      return currentConfig.title;
    }

    if (currentConfig.timePeriodSelection) {
      const tp = currentConfig.timePeriodSelection.timePeriod;
      // For named periods, skip stale saved dates and re-resolve from current
      // dataset availability so the drawer title stays in sync with the layout.
      const isNamedPeriod = getPointInTimeUnitForPeriod(tp) !== null;
      const timePeriodSuffix = getTimePeriodTitleSuffix(
        tp,
        isNamedPeriod ? null : currentConfig.timePeriodSelection.startDate,
        isNamedPeriod ? null : currentConfig.timePeriodSelection.endDate,
        dataAvailabilityDate ?? undefined,
      );
      if (timePeriodSuffix) return `${questions.name}${timePeriodSuffix}`;
    }

    return questions.name;
  }, [questions, currentConfig.title, currentConfig.timePeriodSelection, dataAvailabilityDate]);

  return {
    questions,
    getEmphasisOptions,
    getUsedSlices,
    isValidationError,
    getValidationTooltip,
    isAvailableData,
    headerTitle,
  };
}
